// ── 대시보드 액션 핸들러 훅 ───────────────────────────────────────────────────
// 출근/퇴근/위치/휴게시간 저장/비밀번호 변경/로그아웃 처리
import { apiClient } from '../../api/apiClient';

/**
 * 대시보드 사용자 액션 핸들러 훅
 * @param {object} base  - useDashboardBase 반환값 (상태 + setters)
 * @param {object} extra - { navigate, showToast }
 */
export function useDashboardActions(base, { navigate, showToast }) {
  const {
    currentAddress, locationUsable, addressLoading,
    attendanceYear, attendanceMonth,
    setActionLoading,
    setAttendanceList, setBreakDraftById, setSavingBreakById,
    setLateReasonModalOpen, setLateReasonText, setLatePromptInfo,
    setPwForm, setPwModal, setForcePwChange,
    fetchBase, fetchCurrentAddress, fetchAttendanceRecords,
    latePromptInfo, lateReasonText, pwForm, breakDraftById,
  } = base;

  /** 출근 API 호출 (지각 사유 포함 가능) */
  async function postCheckIn(currentAddressText, lateReason) {
    const body = { current_address: currentAddressText };
    if (lateReason) body.late_reason = lateReason;
    return apiClient.post('/api/attendance/check-in', body);
  }

  /** 출근 처리 (위치 확인 후 출근 요청, 지각 사유 모달 필요 시 표시) */
  async function doCheckIn() {
    if (!locationUsable || addressLoading) {
      showToast('위치 권한 허용 및 현재 위치 확인 후 출근할 수 있습니다.', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const latestAddress = (await fetchCurrentAddress()) || currentAddress || null;
      if (!latestAddress) {
        showToast('현재 위치를 확인할 수 없어 출근 처리할 수 없습니다.', 'error'); return;
      }
      const result = await postCheckIn(latestAddress, null);
      if (!result.ok) {
        const code = result.data?.code;
        if (code === 'LATE_REASON_REQUIRED') {
          // 지각 사유 입력 모달 표시
          setLatePromptInfo({
            pending_address: latestAddress,
            late_minutes: Number.isFinite(Number(result.data?.late_minutes))
              ? Number(result.data?.late_minutes) : null,
            schedule_start_time: result.data?.schedule_start_time || null,
          });
          setLateReasonText('');
          setLateReasonModalOpen(true);
          return;
        }
        showToast(result.data?.message || '출근 처리에 실패했습니다.', 'error');
        await fetchBase();
        return;
      }
      showToast(result.data?.message || '출근이 완료되었습니다.', 'success');
      await fetchBase();
      await fetchAttendanceRecords(attendanceYear, attendanceMonth);
    } finally {
      setActionLoading(false);
    }
  }

  /** 지각 사유 입력 후 출근 처리 */
  async function submitLateReasonCheckIn() {
    const trimmedReason = String(lateReasonText || '').trim();
    if (!trimmedReason) { showToast('지각 사유를 입력해 주세요.', 'error'); return; }
    const pendingAddress = latePromptInfo.pending_address || currentAddress || null;
    if (!pendingAddress) { showToast('현재 위치를 다시 확인해 주세요.', 'error'); return; }
    setActionLoading(true);
    try {
      const result = await postCheckIn(pendingAddress, trimmedReason);
      if (!result.ok) { showToast(result.data?.message || '출근 처리에 실패했습니다.', 'error'); return; }
      setLateReasonModalOpen(false);
      setLateReasonText('');
      setLatePromptInfo({ pending_address: null, late_minutes: null, schedule_start_time: null });
      showToast(result.data?.message || '출근이 완료되었습니다.', 'success');
      await fetchBase();
      await fetchAttendanceRecords(attendanceYear, attendanceMonth);
    } finally {
      setActionLoading(false);
    }
  }

  /** 위치 권한 요청 (차단 시 안내 알림 표시) */
  async function requestCurrentLocation() {
    if (!navigator.geolocation) {
      showToast('브라우저가 위치 기능을 지원하지 않습니다.', 'error'); return;
    }
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'denied') {
          window.alert('위치 권한이 차단되어 있습니다.\n\n브라우저 주소창 왼쪽 자물쇠(또는 사이트 설정) > 위치 > 허용으로 변경한 뒤 다시 눌러주세요.');
          showToast('위치 권한이 차단되어 있습니다. 브라우저 설정에서 허용해 주세요.', 'error');
          return;
        }
      } catch (_error) { /* 권한 API 미지원 시 무시 */ }
    }
    const latestAddress = await fetchCurrentAddress();
    if (latestAddress) { showToast('현재 위치 확인이 완료되었습니다.', 'success'); return; }
    showToast('위치 권한 동의 후 다시 시도해 주세요.', 'error');
  }

  /** 퇴근 처리 */
  async function doCheckOut() {
    if (!locationUsable || addressLoading) {
      showToast('위치 권한 허용 후 현재 위치 확인이 완료되어야 퇴근할 수 있습니다.', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const latestAddress = (await fetchCurrentAddress()) || currentAddress || null;
      if (!latestAddress) {
        showToast('현재 위치를 확인할 수 없어 퇴근 처리할 수 없습니다.', 'error'); return;
      }
      const result = await apiClient.post('/api/attendance/check-out', { current_address: latestAddress });
      if (!result.ok) {
        showToast(result.data?.message || '퇴근 처리에 실패했습니다.', 'error');
        await fetchBase(); return;
      }
      showToast(result.data?.message || '퇴근이 완료되었습니다.', 'success');
      await fetchBase();
      await fetchAttendanceRecords(attendanceYear, attendanceMonth);
    } finally {
      setActionLoading(false);
    }
  }

  /** 휴게시간 수동 저장 */
  async function saveBreakMinutes(row) {
    const rowId = row?.id;
    if (!rowId) return;
    const raw   = breakDraftById[rowId];
    const value = raw === '' ? null : Number(raw);
    if (raw !== '' && (!Number.isFinite(value) || value < 0 || value > 600)) {
      showToast('휴게시간은 0~600분 범위로 입력해 주세요.', 'error'); return;
    }
    setSavingBreakById((prev) => ({ ...prev, [rowId]: true }));
    try {
      const result = await apiClient.put(`/api/attendance/${rowId}/break-minutes`, { breakMinutes: value });
      if (!result.ok) { showToast(result.data?.message || '휴게시간 저장에 실패했습니다.', 'error'); return; }
      const saved = result.data?.row?.break_minutes ?? null;
      setAttendanceList((prev) => prev.map((item) => item.id === rowId ? { ...item, break_minutes: saved } : item));
      setBreakDraftById((prev) => ({ ...prev, [rowId]: saved ?? '' }));
      showToast('휴게시간이 저장되었습니다.', 'success');
    } finally {
      setSavingBreakById((prev) => ({ ...prev, [rowId]: false }));
    }
  }

  /** 비밀번호 변경 */
  async function changePassword() {
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      showToast('현재 비밀번호, 새 비밀번호, 확인 값을 모두 입력해 주세요.', 'error'); return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      showToast('새 비밀번호와 확인 값이 다릅니다.', 'error'); return;
    }
    const result = await apiClient.put('/api/me/password', {
      currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword,
    });
    if (!result.ok) { showToast(result.data?.message || '비밀번호 변경에 실패했습니다.', 'error'); return; }
    showToast(result.data?.message || '비밀번호가 변경되었습니다.', 'success');
    setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPwModal(false);
    setForcePwChange(false);
    fetchBase();
  }

  /** 로그아웃 */
  async function logout(setAccountMenuOpen) {
    if (setAccountMenuOpen) setAccountMenuOpen(false);
    await apiClient.logout();
    navigate('/login');
  }

  return {
    doCheckIn, submitLateReasonCheckIn, requestCurrentLocation,
    doCheckOut, saveBreakMinutes, changePassword, logout,
  };
}
